import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** The admin action on a `banned` account (row and conversation banner) —
 *  also the confirm dialog's title. */
export const BANNED_RETRY_LABEL = '重试连接（保留凭据）';

interface BannedRetryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The account's display label (`formatAccountLabel`). */
  accountLabel: string;
  /** 「确认重试」 — the caller runs `useReconnectAccount` and closes the dialog
   *  when the request settles. */
  onConfirm: () => void;
  /** The retry is in flight — both actions are disabled. */
  pending?: boolean;
}

/**
 * Confirm gate in front of retrying a `banned` number WITH its preserved
 * credentials (`POST /accounts/{id}/reconnect` — the hub re-enables and
 * starts it without a logout). Unlike 重新扫码 nothing is wiped, but it is a
 * bet that WhatsApp lifted its lock, so the dialog says what happens:
 * credentials are kept; a number WhatsApp still blocks is stopped again
 * automatically; restriction locks usually last about 7 days.
 *
 * The first focusable (and so the initially focused) control is 「取消」, so a
 * stray Enter never confirms (same as `ResendConfirmDialog`).
 */
export function BannedRetryDialog({
  open,
  onOpenChange,
  accountLabel,
  onConfirm,
  pending = false,
}: BannedRetryDialogProps): React.JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{BANNED_RETRY_LABEL}</DialogTitle>
          <DialogDescription>
            将保留 {accountLabel} 的登录凭据，重新启用并尝试连接（不退出登录，无需重新扫码）。
          </DialogDescription>
        </DialogHeader>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          <li>如果 WhatsApp 仍在限制该号码，系统会自动再次停止它。</li>
          <li>这类限制锁通常持续约 7 天。</li>
        </ul>
        <DialogFooter className="gap-2 sm:gap-2">
          <DialogClose asChild>
            <Button type="button" variant="ghost" size="sm" disabled={pending}>
              取消
            </Button>
          </DialogClose>
          <Button type="button" size="sm" disabled={pending} onClick={onConfirm}>
            确认重试
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
